import type { DiagramType } from '../diagram/registries';
import {
  findRelationshipSubtypeByPredicate,
  findSubtypeForEdge,
  getRelationshipMapping,
  getRelationshipSubtype,
} from '../diagram/registries';
import type { RelationshipOntologyMapping } from '../diagram/relationship-manifest.types';
import type {
  RelationPredicate,
  RelationFacet,
  RelationQualifiers,
  RelationSemantics,
  RelationLifecycleStatus,
} from './types';

export const RELATION_SEMANTICS_ATTR = '__relationSemantics';
export const RELATION_SUBTYPE_ATTR = '__relationshipSubtype';

const PREDICATE_PATTERN = /^[A-Za-z][A-Za-z0-9_.:-]*$/;

export function isRelationPredicate(
  value: unknown,
): value is RelationPredicate {
  return typeof value === 'string' && PREDICATE_PATTERN.test(value.trim());
}

export function readRelationPredicate(
  value: unknown,
): RelationPredicate | null {
  return isRelationPredicate(value) ? value.trim() : null;
}

export function relationSemanticsFromUnknown(
  value: unknown,
): RelationSemantics | undefined {
  if (!isRecord(value)) return undefined;
  const predicate = readRelationPredicate(value.predicate);
  const status = readLifecycleStatus(value.status);
  const facets = Array.isArray(value.facets)
    ? value.facets.filter((facet): facet is RelationFacet => typeof facet === 'string')
    : undefined;
  const qualifiers = isRecord(value.qualifiers)
    ? { ...value.qualifiers }
    : undefined;
  const originalLabel = typeof value.originalLabel === 'string' && value.originalLabel.trim()
    ? value.originalLabel.trim()
    : undefined;
  if (!predicate && !status && !facets?.length && !qualifiers && !originalLabel) {
    return undefined;
  }
  return compactSemantics({
    predicate: predicate ?? undefined,
    status: status ?? undefined,
    facets,
    qualifiers,
    originalLabel,
  });
}

export function storedRelationSemantics(
  attrs?: Record<string, unknown> | null,
): RelationSemantics | undefined {
  return relationSemanticsFromUnknown(attrs?.[RELATION_SEMANTICS_ATTR]);
}

export function relationSubtypeFromAttrs(
  diagramType: DiagramType,
  edgeType: string,
  attrs?: Record<string, unknown> | null,
): string | undefined {
  const explicit = attrs?.[RELATION_SUBTYPE_ATTR];
  if (typeof explicit === 'string' && getRelationshipSubtype(diagramType, explicit)) {
    return explicit;
  }
  return findSubtypeForEdge(diagramType, edgeType, attrs ?? {})?.id;
}

export function relationSubtypeForPredicate(
  diagramType: DiagramType,
  predicate: string,
): string | undefined {
  return findRelationshipSubtypeByPredicate(diagramType, predicate)?.id;
}

export function relationSemanticsForSubtype(input: {
  diagramType: DiagramType;
  subtypeId: string;
  qualifierSource?: Record<string, unknown>;
  fallbackPredicate?: string;
  originalLabel?: string;
  defaultStatus?: RelationLifecycleStatus;
  existing?: RelationSemantics;
}): RelationSemantics {
  const mapping = getRelationshipMapping(input.diagramType, input.subtypeId);
  return buildRelationSemantics({
    ontology: mapping?.ontology,
    qualifierSource: input.qualifierSource,
    fallbackPredicate: input.fallbackPredicate ?? input.subtypeId,
    originalLabel: input.originalLabel ?? input.subtypeId,
    defaultStatus: input.defaultStatus,
    existing: input.existing,
  });
}

export function buildRelationSemantics(input: {
  ontology?: RelationshipOntologyMapping;
  qualifierSource?: Record<string, unknown>;
  fallbackPredicate?: string;
  originalLabel?: string;
  defaultStatus?: RelationLifecycleStatus;
  existing?: RelationSemantics;
}): RelationSemantics {
  const fallbackPredicate = readRelationPredicate(input.fallbackPredicate);
  const predicate = readRelationPredicate(input.ontology?.predicate) ?? fallbackPredicate;
  const qualifiers = extractQualifiers(
    input.ontology,
    input.qualifierSource,
  );
  const sourceOriginalLabel = readOriginalLabel(input.ontology, input.qualifierSource);
  const inferred = compactSemantics({
    predicate: predicate ?? undefined,
    status: input.ontology?.status ?? input.defaultStatus ?? 'profile_only',
    facets: input.ontology?.facets,
    qualifiers,
    originalLabel: input.originalLabel ?? sourceOriginalLabel,
  });
  return mergeRelationSemantics(inferred, input.existing);
}

export function mergeRelationSemantics(
  inferred: RelationSemantics,
  existing?: RelationSemantics,
): RelationSemantics {
  if (!existing) return inferred;
  return compactSemantics({
    ...inferred,
    ...existing,
    facets: existing.facets ?? inferred.facets,
    qualifiers: {
      ...(inferred.qualifiers ?? {}),
      ...(existing.qualifiers ?? {}),
    },
  });
}

export function stripRelationInternalAttrs(
  attrs?: Record<string, unknown> | null,
): Record<string, unknown> {
  const clean = { ...(attrs ?? {}) };
  delete clean[RELATION_SEMANTICS_ATTR];
  delete clean[RELATION_SUBTYPE_ATTR];
  return clean;
}

export function attrsWithRelationSemantics(
  attrs: Record<string, unknown>,
  semantics?: RelationSemantics,
  subtypeId?: string,
): Record<string, unknown> {
  return {
    ...attrs,
    ...(subtypeId ? { [RELATION_SUBTYPE_ATTR]: subtypeId } : {}),
    ...(semantics ? { [RELATION_SEMANTICS_ATTR]: semantics } : {}),
  };
}

function extractQualifiers(
  ontology?: RelationshipOntologyMapping,
  source?: Record<string, unknown>,
): RelationQualifiers | undefined {
  const qualifiers: RelationQualifiers = {
    ...(ontology?.defaultQualifiers ?? {}),
  };
  if (source) {
    for (const key of ontology?.qualifierKeys ?? []) {
      const value = source[key];
      if (value !== undefined) qualifiers[key] = value;
    }
  }
  return Object.keys(qualifiers).length > 0 ? qualifiers : undefined;
}

function readOriginalLabel(
  ontology?: RelationshipOntologyMapping,
  source?: Record<string, unknown>,
): string | undefined {
  const key = ontology?.originalLabelKey;
  if (!key || !source) return undefined;
  const value = source[key];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function compactSemantics(semantics: RelationSemantics): RelationSemantics {
  const out: RelationSemantics = {};
  if (semantics.predicate) out.predicate = semantics.predicate;
  if (semantics.status) out.status = semantics.status;
  if (semantics.facets?.length) out.facets = semantics.facets;
  if (semantics.qualifiers && Object.keys(semantics.qualifiers).length > 0) {
    out.qualifiers = semantics.qualifiers;
  }
  if (semantics.originalLabel) out.originalLabel = semantics.originalLabel;
  return out;
}

function readLifecycleStatus(value: unknown): RelationLifecycleStatus | null {
  return value === 'draft' ||
    value === 'profile_only' ||
    value === 'exchange_only' ||
    value === 'promotable' ||
    value === 'projection_enabled' ||
    value === 'deprecated'
    ? value
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
